import './index.scss'

import React, { useState, useEffect, useRef } from 'react'
import { DateTime } from 'luxon'
import Page from '../../../Components/Page'
import API, { RFCommand, RFPacket } from '../../../API'
import { Button, FormGroup, H5, InputGroup } from '@blueprintjs/core'

export default function AdminGSMTools() {
	const [connections, setConnections] = useState(API.connections)
	const [selectedAgentId, setSelectedAgentId] = useState(0)

	useEffect(() => {
		const subscribeToCommandBridge = async () => {
			try {
				await API.call('Admin.CommandBridge.Subscribe')
			} catch (error) {
				console.error('Failed to subscribe:', error)
			}
		}

		// Initial fetch
		subscribeToCommandBridge()

		// Set up interval for periodic fetching
		const intervalId = setInterval(subscribeToCommandBridge, 7000)

		return () => {
			clearInterval(intervalId)
		}
	}, [])

	useEffect(() => {
		setConnections(API.connections)
	}, [API.connections])

	return (
		<Page title="Admin GSM Tools" className="AdminGSMToolsPage" icon="fa-solid fa-mobile-signal-out">
			<PacketSender connections={connections} onSelectAgentId={(id) => setSelectedAgentId(id)} />
			<RFLog connections={connections} selectedAgentId={selectedAgentId} />
		</Page>
	)
}

function PacketSender({ connections, onSelectAgentId }) {
	const formRef = useRef(null)

	const send = async () => {
		const formData = new FormData(formRef.current)

		let command = parseInt(formData.get('command'), 10)
		let hex = formData.get('payload')
		hex = hex.replace(/[^0-9a-fA-F]/g, '')

		let agentId = formData.get('gateway')
		const sendToAgents = []
		agentId = parseInt(agentId, 10)
		for (let con of API.connections) {
			if (con.id == agentId) {
				sendToAgents.push(con)
			}
		}

		for (let con of sendToAgents) {
			await API.call('Admin.CommandBridge.Agent.Send', {
				agentId: con.id,
				type: command,
				payloadHex: hex
			})
		}
	}

	const commandList = []
	for (let name in RFCommand) {
		const code = RFCommand[name]
		if (typeof code === 'number') {
			const nameStr = name + ' (#' + code + ')'
			commandList.push({ code: RFCommand[name], name: nameStr })
		}
	}

	return (
		<div className="PacketSender">
			<H5>Command Sender</H5>
			<form ref={formRef}>
				<div className="Inputs">
					<FormGroup label="Gateway" labelFor="gateway">
						<select id="gateway" name="gateway" onChange={(e) => onSelectAgentId(parseInt(e.target.value, 10))}>
							<option value="0">Show All Traffic</option>
							{connections.map((con) => (
								<option value={con.id} key={`csocon${con.id}`}>
									{con.scanReport?.deviceId}
								</option>
							))}
						</select>
					</FormGroup>

					<FormGroup label="Command (DEC)" labelFor="command">
						<InputGroup id="command" name="command" placeholder="" />
					</FormGroup>

					<FormGroup label="Payload (HEX)" labelFor="payload">
						<InputGroup id="payload" name="payload" placeholder="" />
					</FormGroup>
				</div>
				<Button intent="primary" onClick={send}>
					Send
				</Button>
			</form>
		</div>
	)
}

function RFLog({ connections, selectedAgentId }) {
	const [packets, setPackets] = useState([])
	const acRef = useRef(0)

	useEffect(() => {
		const listener = (p) => {
			setPackets((prevPackets) => limitArraySize([p, ...prevPackets], 250))
		}

		API.addRFListener(null, null, listener)

		return () => {
			API.removeApiListener(null)
		}
	}, [])

	const limitArraySize = (arr, size) => {
		if (arr.length > size) {
			arr.splice(size, arr.length - size)
		}
		return arr
	}

	useEffect(() => {
		const subscribeToCommandBridge = async () => {
			try {
				await API.call('Admin.CommandBridge.Subscribe')
			} catch (error) {
				console.error('Failed to subscribe:', error)
			}
		}

		// Initial fetch
		subscribeToCommandBridge()

		// Set up interval for periodic fetching
		const intervalId = setInterval(subscribeToCommandBridge, 7000)

		return () => {
			clearInterval(intervalId)
		}
	}, [])

	const clear = () => {
		setPackets([])
	}

	let displayPackets = packets
	if (selectedAgentId) {
		displayPackets = packets.filter((p) => p.agentId == selectedAgentId)
	}

	return (
		<div className="RFLog">
			<H5>
				Live Traffic <Button size="small" variant="minimal" icon="trash" onClick={clear} />
			</H5>
			<table className="bp5-html-table bp5-compact bp5-html-table-bordered bp5-html-table-striped">
				<thead>
					<tr>
						<th width="100">Time</th>
						<th width="10">DIR</th>
						<th width="160">Gateway</th>
						<th width="70">Command</th>
						<th>Payload</th>
					</tr>
				</thead>
				<tbody>
					{displayPackets.map((p, idx) => {
						let conInfo = '? ' + p.agentId + ' ?'
						for (let con of connections) {
							if (con.id == p.agentId) {
								p.connection = con
								if (con.scanReport) {
									conInfo = con.scanReport.deviceId
								}
							}
						}
						return (
							<tr key={p.id + '-' + idx}>
								<td>{DateTime.fromMillis(p.time ?? Date.now()).toFormat('HH:mm:ss.SSS')}</td>
								<td>{p.direction}</td>
								<td>{conInfo}</td>
								<td>#{p.type}</td>
								<td>{p.payloadHex}</td>
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}
